import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Paper,
  Tab,
  Tabs,
  Typography,
  Stack,
  Select,
  MenuItem,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Publisher } from '../types/publisher.types';
import { getPublisherById } from '../features/publishers/api';
import { Edit as EditIcon, Language as LanguageIcon, Person as PersonIcon } from '@mui/icons-material';
import WebsitesTable from '../features/websites/components/WebsitesTable';
import JournalistsTable from '../features/journalists/components/JournalistsTable';
import NotificationSnackbar, { NotificationType } from '../components/common/NotificationSnackBar';
import PublisherForm from 'features/publishers/components/common/PublisherForm';
import { Newspaper as NewspaperIcon } from '@mui/icons-material';
import ArticlesTable from '../features/articles/components/ArticlesTable';
import { Event as EventIcon } from '@mui/icons-material';
import EventsTable from '../features/events/components/EventsTable';
import { RecordVoiceOver as VoiceIcon } from '@mui/icons-material';
import PronunciationRulesTab from 'features/publishers/components/PronunciationRulesTab';
import { PermMedia as MediaIcon } from '@mui/icons-material';
import MediaTable from '../features/media/components/MediaTable';
import { Campaign as CampaignIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { listIssueCasts, type IssueCastListItem } from 'api/apiClient';
import IssueCastList from 'features/issuecasts/components/IssueCastList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`publisher-tabpanel-${index}`}
      aria-labelledby={`publisher-tab-${index}`}
      {...other}
      style={{
        padding: '16px 0',
        display: value === index ? 'block' : 'none'
      }}
    >
      {children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `publisher-tab-${index}`,
    'aria-controls': `publisher-tabpanel-${index}`,
  };
}

const PublisherIssueCastsTab: React.FC<{ publisherPK: string }> = ({ publisherPK }) => {
  const [items, setItems] = useState<IssueCastListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [maxItems, setMaxItems] = useState<number>(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listIssueCasts({ publisherId: publisherPK, maxItems });
      setItems(data || []);
    } catch {
      // surface list errors via list component notifications only during actions; for load, keep silent UI
    } finally {
      setLoading(false);
    }
  }, [publisherPK, maxItems]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <IssueCastList items={items} setItems={setItems} loading={loading} />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Select size="small" value={maxItems} onChange={(e) => setMaxItems(Number(e.target.value))}>
            <MenuItem value={50}>Max 50</MenuItem>
            <MenuItem value={100}>Max 100</MenuItem>
            <MenuItem value={200}>Max 200</MenuItem>
            <MenuItem value={500}>Max 500</MenuItem>
          </Select>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

const PublisherDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [publisher, setPublisher] = useState<Publisher | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info' as NotificationType,
  });

  const fetchPublisherDetails = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const publisherData = await getPublisherById(id);
      setPublisher(publisherData);
      setLogoError(false); // Reset logo error when fetching new publisher
    } catch (error) {
      console.error('Failed to fetch publisher details:', error);
      setNotification({
        open: true,
        message: 'Failed to load publisher details',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPublisherDetails();
    }
  }, [id, fetchPublisherDetails]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditPublisher = () => {
    setOpenEditForm(true);
  };

  const handleCloseEditForm = () => {
    setOpenEditForm(false);
    fetchPublisherDetails();
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!publisher && !isLoading) {
    return (
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Publisher not found
        </Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to="/publishers"
        >
          Back to Publishers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" component={RouterLink} to="/">
          Home
        </Link>
        <Link underline="hover" color="inherit" component={RouterLink} to="/publishers">
          Publishers
        </Link>
        <Typography color="text.primary">{publisher?.name}</Typography>
      </Breadcrumbs>

      {publisher && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" component="h1">
                  {publisher.name}
                </Typography>
                {publisher.tagline && (
                  <Typography variant="subtitle1" color="text.secondary">
                    {publisher.tagline}
                  </Typography>
                )}
              </Box>
              {publisher.logoUrl && !logoError && (
                <Box
                  component="img"
                  src={publisher.logoUrl}
                  alt={publisher.name}
                  sx={{
                    maxHeight: 64,
                    maxWidth: 256,
                    mr: 2,
                    ml:2,
                    objectFit: 'contain',
                  }}
                  onError={() => {
                    setLogoError(true);
                  }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditPublisher}
            >
              Edit Publisher
            </Button>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="publisher tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                icon={<LanguageIcon />}
                iconPosition="start"
                label="News Sources"
                {...a11yProps(0)}
              />
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label="Journalists"
                {...a11yProps(1)}
              />
              <Tab
                icon={<NewspaperIcon />}
                iconPosition="start"
                label="Articles"
                {...a11yProps(2)}
              />
              <Tab
                icon={<EventIcon />}
                iconPosition="start"
                label="Events"
                {...a11yProps(3)}
              />
              <Tab
                icon={<MediaIcon />}
                iconPosition="start"
                label="Media"
                {...a11yProps(4)}
              />
              <Tab
                icon={<VoiceIcon />}
                iconPosition="start"
                label="Pronunciation Rules"
                {...a11yProps(5)}
              />
              <Tab
                icon={<CampaignIcon />}
                iconPosition="start"
                label="Podcasts"
                {...a11yProps(6)}
              />
            </Tabs>
            <TabPanel value={tabValue} index={0}>
              <WebsitesTable publisherId={id} />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <JournalistsTable publisherId={id} />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <ArticlesTable publisherId={id} />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <EventsTable publisherId={id} />
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              <MediaTable publisherId={id} />
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
              <PronunciationRulesTab
                publisherId={id!}
                onChanged={fetchPublisherDetails}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={6}>
              {id ? (
                <PublisherIssueCastsTab publisherPK={`PUBLISHER#${id}`} />
              ) : (
                <Typography color="text.secondary">Missing publisher id.</Typography>
              )}
            </TabPanel>
          </Box>

          <PublisherForm
            open={openEditForm}
            onClose={handleCloseEditForm}
            publisher={publisher}
            isEdit
          />
        </>
      )}

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
    </Box>
  );
};

export default PublisherDetailPage;